import React, { useState, useEffect, useMemo, useRef, startTransition } from 'react';
import ReactDOM from 'react-dom/client';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { format } from 'date-fns';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CreateNewTask from '../../components/common/CreateNewTask';
import Timeline from '../../components/common/Timeline';
import TimelineItemCard from '../../components/common/TimelineItemCard';
import MonthViewCard from './MonthViewCard';
import WeekViewCard from './WeekViewCard';
import DayViewCard from './DayViewCard';
import { useTask } from '../../contexts/TaskContext';
import { useModal } from '../../contexts/ModalContext';
import { useAccount } from '../../contexts/AccountContext';
import { getTaskStatusColor } from '../../utils/helper';
import { fetchAgentSelect } from '../../services/common';
import teamService from '../../services/teamService';

// Lazy load FullCalendar to prevent blocking initial render
const FullCalendar = React.lazy(() => import('@fullcalendar/react').then(module => ({ default: module.default })));

// Styled components
const CalendarWrapper = styled(Box)(({ theme, currentView }) => ({
  height: 'auto',
  minWidth: '100%',
  // Conditional horizontal scroll only for month view
  ...(currentView === 'dayGridMonth' && {
    overflowX: 'auto',
    overflowY: 'hidden',
    // Custom scrollbar styling
    '&::-webkit-scrollbar': {
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#F9FAFB',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#D1D5DB',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#9CA3AF',
    },
  }),
  // For other views, no horizontal scroll
  ...(currentView !== 'dayGridMonth' && {
    overflowX: 'visible',
    overflowY: 'visible',
  }),
  '& .fc': {
    fontFamily: theme.typography.fontFamily,
    height: 'auto',
    '& .fc-view-harness': {
      height: 'auto !important',
    },
    '--fc-border-color': '#E2E8F0',
    '--fc-button-text-color': theme.palette.text.primary,
    '--fc-button-bg-color': theme.palette.background.paper,
    '--fc-button-border-color': '#E2E8F0',
    '--fc-button-hover-bg-color': theme.palette.action.hover,
    '--fc-button-hover-border-color': theme.palette.primary.main,
    '--fc-button-active-bg-color': theme.palette.primary.main,
    '--fc-button-active-border-color': theme.palette.primary.main,
    '--fc-today-bg-color': '#F1F5F9',
    '--fc-page-bg-color': '#fff',
    '--fc-neutral-bg-color': '#fff',
  },
  '& .fc table': {
    tableLayout: 'fixed',
    width: '100%',
    // Conditional minimum width based on view
    ...(currentView === 'dayGridMonth' && {
      minWidth: '1120px', // 7 columns × 160px for month view
    }),
    ...(currentView !== 'dayGridMonth' && {
      minWidth: 'auto', // Flexible width for other views
    }),
  },
  '& .fc-view-harness': {
    ...(currentView === 'dayGridMonth' && {
      minWidth: '1120px',
    }),
    ...(currentView !== 'dayGridMonth' && {
      minWidth: 'auto',
    }),
  },
  '& .fc .fc-daygrid-day-frame': {
    padding: '2px',
    minHeight: '200px',
    // Conditional sizing based on view
    ...(currentView === 'dayGridMonth' && {
      width: '14.285714%', // 100% / 7 columns = equal width
      minWidth: '160px',
    }),
    ...(currentView !== 'dayGridMonth' && {
      width: 'auto',
      minWidth: 'auto',
    }),
  },
  
  // Hide default FullCalendar popover completely
  '& .fc-popover': {
    display: 'none !important',
  },
  '& .fc-more-popover': {
    display: 'none !important',
  },
  '& .fc-popover-header': {
    display: 'none !important',
  },
  '& .fc-popover-body': {
    display: 'none !important',
  },
  '& .fc-popover-title': {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#111827',
  },
  '& .fc-popover-close': {
    color: '#6B7280',
    cursor: 'pointer',
    fontSize: '1rem',
    '&:hover': {
      color: '#111827',
    }
  },
  '& .fc-popover-body': {
    padding: '12px',
    maxHeight: '500px',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#F9FAFB',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#D1D5DB',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#9CA3AF',
    },
  },
  '& .fc-popover-body .fc-daygrid-event-harness': {
    marginBottom: '12px',
    minHeight: '100px !important',
  },
  '& .fc-popover-body .fc-event': {
    margin: '6px 0',
    width: '100%',
    minHeight: '100px',
  },
  // Force popover events to use TimelineItemCard styling
  '& .fc-popover .fc-event': {
    backgroundColor: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    minHeight: '100px !important',
    maxHeight: 'none !important',
  },
  '& .fc .fc-toolbar': {
    marginBottom: theme.spacing(2),
  },
  '& .fc .fc-button': {
    textTransform: 'capitalize',
    fontWeight: 500,
    padding: '6px 12px',
  },
  '& .fc .fc-daygrid-day': {
    height: 200,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    transition: 'all 0.2s ease',
    // Conditional sizing based on view
    ...(currentView === 'dayGridMonth' && {
      width: '14.285714%', // 100% / 7 columns = equal width
      minWidth: '160px',
    }),
    ...(currentView !== 'dayGridMonth' && {
      width: 'auto',
      minWidth: 'auto',
    }),
    '&:hover': {
      backgroundColor: '#F8FAFC',
    }
  },
  '& .fc .fc-daygrid-day-events': {
    margin: 0,
    padding: '1px',
    width: '100%',
  },
  '& .fc-daygrid-event-harness': {
    margin: '1px 0 !important',
    minHeight: '24px !important',
    maxHeight: '28px !important',
    width: '100% !important',
  },
  '& .fc-daygrid-event': {
    minHeight: '24px !important',
    maxHeight: '28px !important',
    height: 'auto !important',
    width: '100% !important',
  },
  '& .fc-more-link': {
    fontSize: '0.6875rem', // Reduced from 0.75rem
    color: theme.palette.primary.main,
    padding: '2px 4px',
    margin: '2px 0',
    '&:hover': {
      textDecoration: 'underline',
    }
  },
  '& .fc .fc-col-header-cell': {
    padding: theme.spacing(1.5),
    backgroundColor: '#F8FAFC',
    borderBottom: '1px solid #E2E8F0',
    borderRight: '1px solid #E2E8F0',
    // Conditional sizing based on view
    ...(currentView === 'dayGridMonth' && {
      width: '14.285714%', // 100% / 7 columns = equal width
      minWidth: '160px',
    }),
    ...(currentView !== 'dayGridMonth' && {
      width: 'auto',
      minWidth: 'auto',
    }),
  },
  '& .fc .fc-col-header-cell-cushion': {
    padding: '8px 4px',
    color: '#475569',
    fontWeight: 700,
    textTransform: 'uppercase',
    fontSize: '0.6875rem', // Reduced from 0.75rem
    letterSpacing: '0.5px',
  },
  '& .fc .fc-daygrid-day-number': {
    fontSize: '0.8125rem', // Reduced from 0.875rem
    padding: '6px', // Reduced padding
    color: '#1E293B',
    fontWeight: 600,
  },
  '& .fc .fc-day-today': {
    backgroundColor: '#F1F5F9 !important',
  },
  '& .fc .fc-day-today .fc-daygrid-day-number': {
    background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
    color: '#fff',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '4px',
    fontWeight: 700,
    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
  },
  '& .fc-event': {
    border: 'none',
    backgroundColor: 'transparent',
    margin: '2px 4px',
    padding: '0',
    width: 'auto !important',
    minWidth: '100%',
  },
  '& .fc td, & .fc th': {
    border: '1px solid #E2E8F0',
    // Conditional sizing based on view
    ...(currentView === 'dayGridMonth' && {
      width: '14.285714%', // 100% / 7 columns = equal width
      minWidth: '160px',
    }),
    ...(currentView !== 'dayGridMonth' && {
      width: 'auto',
      minWidth: 'auto',
    }),
  },
  '& .fc-timegrid-slot': {
    height: '100px !important',
    backgroundColor: '#FFFFFF',
  },
  '& .fc-timegrid-slot-lane': {
    backgroundColor: '#FFFFFF',
  },
  '& .fc-timegrid-event-harness': {
    margin: '1px 2px !important',
    minHeight: '24px !important',
    maxHeight: '28px !important',
  },
  '& .fc-timegrid-event': {
    backgroundColor: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    minHeight: '24px !important',
    maxHeight: '28px !important',
  },
  '& .fc-col-header-cell': {
    backgroundColor: '#FFFFFF',
    padding: '8px',
    '& .fc-col-header-cell-cushion': {
      color: '#1F2937',
      textDecoration: 'none',
      fontWeight: 600,
    }
  },
  '& .fc-day-today': {
    backgroundColor: '#FFFFFF !important',
    '& .fc-daygrid-day-number': {
      backgroundColor: theme.palette.primary.main,
      color: '#FFFFFF',
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }
  },
  '& .fc-timegrid-axis': {
    borderRight: '1px solid #E5E7EB',
  },
  '& .fc-timegrid-slot-label-cushion': {
    fontSize: '0.85rem',
    color: '#6B7280',
    fontWeight: '500',
  },
  '& .fc-timegrid-now-indicator-line': {
    borderColor: theme.palette.error.main,
    borderWidth: '2px',
    zIndex: 4,
    position: 'relative',
    overflow: 'visible !important',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '0px',
      top: '-5px',
      width: '10px',
      height: '10px',
      backgroundColor: theme.palette.error.main,
      borderRadius: '50%',
      zIndex: 6,
      border: '1px solid #fff',
    }
  },
  '& .fc-timegrid-now-indicator-arrow': {
    borderColor: theme.palette.error.main,
    borderWidth: '6px',
    zIndex: 5,
  },

  '& .fc-more-link': {
    backgroundColor: '#F3F4F6',
    padding: '2px 4px',
    borderRadius: '4px',
    color: '#6B7280',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-block',
    margin: '1px 0',
    border: '1px solid #E5E7EB',
    '&:hover': {
      backgroundColor: '#E5E7EB',
      textDecoration: 'none',
      color: '#374151',
    }
  },

  '& .fc-daygrid-day.fc-day-other': {
    backgroundColor: '#F8FAFC',
  },
  '& .fc-daygrid-day.fc-day-past': {
    backgroundColor: '#FFFFFF',
  },
  '& .fc-daygrid-day.fc-day-future': {
    backgroundColor: '#FFFFFF',
  }
}));

const TimelineItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(3),
  cursor: 'pointer',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .date-box': {
    width: 45,
    textAlign: 'center',
    marginRight: theme.spacing(2),
  },
  '& .day-name': {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(0.5),
  },
  '& .day-number': {
    fontSize: '2rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    lineHeight: 1,
  },
  '& .task-content': {
    flex: 1,
    paddingRight: theme.spacing(3),
  },
  '& .task-title': {
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: '#0F172A',
    marginBottom: theme.spacing(0.5),
  },
  '& .task-time': {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    '& svg': {
      fontSize: '0.875rem',
      marginRight: theme.spacing(0.5),
    }
  },
  '& .arrow-icon': {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    color: '#4F46E5',
  }
}));

// Update the formatTimelineDate function
const formatTimelineDate = (dateString) => {
  try {
    // Ensure we have a valid date string
    if (!dateString) return null;
    
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) return null;

    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    return {
      dayName: isToday ? 'Today' : format(date, 'EEE'),
      dayNumber: format(date, 'dd'),
      time: format(date, 'HH:mm'),
      isToday
    };
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
};


const TaskCalendar = () => {
  // First declare all state variables
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarHeight, setCalendarHeight] = useState('calc(100vh - 120px)');
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const [taskCounts, setTaskCounts] = useState([]);
  const [dateTypeTasks, setDateTypeTasks] = useState([]);
  const [dateTypeDialogOpen, setDateTypeDialogOpen] = useState(false);
  const [selectedDateType, setSelectedDateType] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  // Filter states
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [agents, setAgents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const {openErrorModal} = useModal();
  const { accountDetails, accountId } = useAccount();
  const location = useLocation();
  
  // Then declare refs
  const calendarRef = React.useRef(null);
  const hasFetchedTasks = useRef(false);

  // Then fetch/API related hooks
  const { getStatusColors, createTask, updateTask, deleteTask, fetchCalenderTasks, fetchCalenderTaskCounts, fetchTasksByDateAndType, calenderTasks, loading } = useTask();

  // Now we can use upcomingTasks
  const upcomingTasks = useMemo(() => {
    return calenderTasks
      .filter(task => new Date(task.start_time) >= new Date())
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  }, [calenderTasks]);

  // Get today's tasks
  const todayTasks = useMemo(() => {
    console.log('Computing todayTasks, calenderTasks:', calenderTasks.length);
    const today = new Date();
    const tasks = calenderTasks
      .filter(task => {
        const taskDate = new Date(task.start_time);
        return taskDate.toDateString() === today.toDateString();
      })
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

    console.log('Today tasks before filter:', tasks.length, 'activeSubTab:', activeSubTab);

    // Filter tasks based on activeSubTab
    switch (activeSubTab) {
      case 1: // Calls
        return tasks.filter(task => task.item_type === 'contact_call');
      case 2: // Follow-ups
        return tasks.filter(task => task.item_type === 'lead_followup');
      case 3: // Site Visits
        return tasks.filter(task => task.item_type === 'site_visit');
      case 4: // General
        return tasks.filter(task => task.item_type === 'task' || task.item_type === 'meeting');
      default: // All
        return tasks;
    }
  }, [calenderTasks, activeSubTab]);

  // Get backlog tasks (tasks that are past due)
  const backlogTasks = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return calenderTasks
      .filter(task => {
        const taskDate = new Date(task.start_time);
        taskDate.setHours(0, 0, 0, 0);
        return taskDate < today && (task.status?.toLowerCase() === 'pending' || task.status?.toLowerCase() === 'scheduled');
      })
      .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
  }, [calenderTasks]);

  // Now we can safely use upcomingTasks in useEffect
  useEffect(() => {
    if (!selectedDate) {
      setTimelineEvents(upcomingTasks.slice(0, 5));
    }
  }, [upcomingTasks, selectedDate]);

  // Fetch agents and teams on mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoadingAgents(true);
        const agentData = await fetchAgentSelect();
        setAgents(agentData || []);
      } catch (error) {
        console.error('Error fetching agents:', error);
        setAgents([]);
      } finally {
        setLoadingAgents(false);
      }

      if (accountId) {
        try {
          setLoadingTeams(true);
          const response = await teamService.getTeams(accountId);
          console.log('Teams API response:', response);
          console.log('Response data:', response.data);
          console.log('Response data type:', typeof response.data);
          console.log('Is array?', Array.isArray(response.data));
          
          // Handle different response types
          let teamsData = [];
          if (Array.isArray(response.data)) {
            teamsData = response.data;
          } else if (typeof response.data === 'string') {
            // Backend might return error message as string
            console.error('Backend returned error message:', response.data);
            teamsData = [];
          } else if (response.data && typeof response.data === 'object') {
            // Try to extract array from nested structure
            teamsData = response.data.results || response.data.data || response.data.teams || [];
          }
          
          console.log('Processed teams data:', teamsData, 'Account ID:', accountId);
          
          if (Array.isArray(teamsData) && teamsData.length > 0) {
            setTeams(teamsData);
          } else {
            console.warn('No teams found or teams data is not an array:', teamsData);
            setTeams([]);
          }
        } catch (error) {
          console.error('Error fetching teams:', error);
          console.error('Error status:', error.response?.status);
          console.error('Error details:', error.response?.data);
          
          // Check if it's a 401 Unauthorized (role-based access issue)
          if (error.response?.status === 401) {
            console.warn('Unauthorized: User may not have permission to view teams (requires ADMIN or MANAGER role)');
          }
          
          setTeams([]);
        } finally {
          setLoadingTeams(false);
        }
      } else {
        console.log('Account ID not available yet, accountId:', accountId, 'accountDetails:', accountDetails);
      }
    };

    loadFilters();
  }, [accountId]);

  // Build filter object for API calls
  const buildFilters = useMemo(() => {
    const filters = {};
    
    if (selectedAgent) {
      filters.agent_id = selectedAgent;
    } else if (selectedTeam) {
      filters.team_id = selectedTeam;
    }
    
    return filters;
  }, [selectedAgent, selectedTeam]);

  // Fetch data immediately when component mounts or route changes to calendar
  useEffect(() => {
    // When navigating to calendar page
    if (location.pathname === '/calendar') {
      // Show initial load state only if we have no cached data
      if (calenderTasks.length === 0 && !hasFetchedTasks.current) {
        setIsInitialLoad(true);
      }
      
      // Only fetch if not already loading to avoid duplicate requests
      if (!loading && !hasFetchedTasks.current) {
        hasFetchedTasks.current = true;
        
        const filters = buildFilters;
        
        // Fetch counts FIRST (faster, smaller payload) - don't wait
        fetchCalenderTaskCounts(filters).then((counts) => {
          if (counts) {
            setTaskCounts(counts);
          }
        }).catch(err => {
          console.error('Error fetching counts:', err);
        });
        
        // Fetch full tasks separately (for sidebar) - runs in parallel
        fetchCalenderTasks(filters).catch(err => {
          console.error('Error fetching tasks:', err);
          setIsInitialLoad(false);
        });
      } else if (loading) {
        // Already fetching, just wait for it to complete
        hasFetchedTasks.current = true;
        setIsInitialLoad(true);
      }
    } else {
      // Reset flags when navigating away from calendar
      hasFetchedTasks.current = false;
      setIsInitialLoad(false);
    }
  }, [location.pathname, fetchCalenderTasks, fetchCalenderTaskCounts, calenderTasks.length, loading, buildFilters]);

  // Refetch data when filters change (including when cleared)
  useEffect(() => {
    if (location.pathname === '/calendar' && hasFetchedTasks.current) {
      setIsFilterLoading(true);
      const filters = buildFilters;
      
      // Reset and refetch with new filters (empty filters = show all)
      hasFetchedTasks.current = false;
      
      Promise.all([
        fetchCalenderTaskCounts(filters).then((counts) => {
          if (counts) {
            setTaskCounts(counts);
          }
          return counts;
        }).catch(err => {
          console.error('Error fetching counts:', err);
          return null;
        }),
        fetchCalenderTasks(filters).catch(err => {
          console.error('Error fetching tasks:', err);
          return null;
        })
      ]).finally(() => {
        setIsFilterLoading(false);
      });
    }
  }, [selectedAgent, selectedTeam, buildFilters, location.pathname, fetchCalenderTasks, fetchCalenderTaskCounts]);

  // Track when loading completes - only affects sidebar now
  useEffect(() => {
    if (!loading && hasFetchedTasks.current) {
      // Hide loader when data is loaded
      setIsInitialLoad(false);
    }
  }, [loading]);

  // Also clear initial load when we have tasks
  useEffect(() => {
    if (calenderTasks.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [calenderTasks.length, isInitialLoad]);

  // Show timeout message if loading takes too long
  useEffect(() => {
    if (loading || isInitialLoad) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 5000); // Show message after 5 seconds
      
      return () => {
        clearTimeout(timer);
        setLoadingTimeout(false);
      };
    } else {
      setLoadingTimeout(false);
    }
  }, [loading, isInitialLoad]);

  // Update calendar height when calendar content changes
  useEffect(() => {
    const updateCalendarHeight = () => {
      // Try to get the calendar container by ID or class
      const calendarContainer = document.querySelector('[data-calendar-container="true"]');
      if (calendarContainer) {
        const height = calendarContainer.offsetHeight;
        setCalendarHeight(`${height}px`);
        return;
      }
      
      // Fallback to FullCalendar element
      if (calendarRef.current) {
        const calendarElement = calendarRef.current.getApi().el;
        if (calendarElement) {
          // Get the full calendar container height including header
          const calendarWrapper = calendarElement.closest('.MuiBox-root');
          const height = calendarWrapper ? calendarWrapper.offsetHeight : calendarElement.offsetHeight;
          setCalendarHeight(`${height}px`);
        }
      }
    };

    // Update height after multiple delays to ensure calendar is fully rendered
    const timer1 = setTimeout(updateCalendarHeight, 100);
    const timer2 = setTimeout(updateCalendarHeight, 300);
    const timer3 = setTimeout(updateCalendarHeight, 500);
    const timer4 = setTimeout(updateCalendarHeight, 1000);
    
    // Also set up a ResizeObserver to track height changes
    let resizeObserver;
    if (calendarRef.current) {
      const calendarElement = calendarRef.current.getApi().el;
      if (calendarElement) {
        const calendarWrapper = calendarElement.closest('.MuiBox-root');
        if (calendarWrapper) {
          resizeObserver = new ResizeObserver(() => {
            updateCalendarHeight();
          });
          resizeObserver.observe(calendarWrapper);
        }
      }
    }
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [calenderTasks, currentView]);

  // Helper function to convert item_type to verbose format
  const getItemTypeVerbose = (itemType) => {
    const typeMap = {
      'initial_contact': 'Initial Contact',
      'f2f_meeting': 'F2F Meeting',
      'virtual_meeting': 'Virtual Meeting',
      'lead_followup': 'Lead Followup',
      'site_visit': 'Site Visit',
      'document_collection': 'Document Collection',
      'task': 'Task',
      'whatsapp_message': 'Whatsapp Message',
      'contact_call': 'Contact Call',
      'other': 'Other'
    };
    return typeMap[itemType] || itemType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Convert task counts to calendar events for month/week views
  const eventsFromCounts = useMemo(() => {
    if (!taskCounts || taskCounts.length === 0) return [];
    
    const events = [];
    taskCounts.forEach(dateData => {
      const date = dateData.date;
      const detailedCounts = dateData.detailed_counts || dateData.counts;
      
      // Create an event for each task type on this date
      Object.entries(detailedCounts).forEach(([itemType, countData]) => {
        // Handle both old format (just number) and new format (object with total/completed)
        const total = typeof countData === 'object' ? countData.total : countData;
        const completed = typeof countData === 'object' ? countData.completed : 0;
        const completionPercentage = total > 0 ? Math.round((completed / total) * 100) : 0;
        
        // Create a pseudo-event for display in month/week view
        events.push({
          id: `${date}-${itemType}`,
          title: `${getItemTypeVerbose(itemType)} (${total})`,
          start: date,
          end: date,
          allDay: true,
          extendedProps: {
            item_type: itemType,
            item_type_verbose: getItemTypeVerbose(itemType),
            count: total,
            total: total,
            completed: completed,
            completionPercentage: completionPercentage,
            isCountEvent: true
          },
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          editable: false
        });
      });
    });
    
    return events;
  }, [taskCounts]);

  // Use counts for month/week views, full tasks for other views
  const events = useMemo(() => {
    // For month and week views, use count-based events
    if (currentView === 'dayGridMonth' || currentView === 'timeGridWeek') {
      return eventsFromCounts;
    }
    
    // For other views, use full task data
    return calenderTasks.map(task => ({
      id: task.id,
      title: task.title,
      start: task.start_time,
      end: task.end_time,
      extendedProps: {
        assigned_to: task.assigned_to,
        owner: task.owner,
        priority: task.priority,
        description: task.description,
        status: task.status,
        lead: task.lead,
        ...task
      },
      backgroundColor: getStatusColors(task.status).bg,
      borderColor: getStatusColors(task.status).border,
      editable: !['completed', 'cancelled'].includes(task.status?.toLowerCase())
    }));
  }, [calenderTasks, eventsFromCounts, currentView, getStatusColors]);

  const handleDateClick = (arg) => {
    setSelectedDate(arg.date);
    setSelectedEvent(null);
    setCreateDialogOpen(true);
  };

  const handleEventClick = (arg) => {
    // Don't open dialog for count events
    if (arg.event.extendedProps.isCountEvent) {
      return;
    }
    setSelectedEvent(arg.event.extendedProps);
    setCreateDialogOpen(true);
  };

  // Wrapper function for card clicks that transforms item data to expected format
  const handleCardClick = (item) => {
    setSelectedEvent(item);
    setCreateDialogOpen(true);
  };

  // Handle click on count event card to fetch tasks
  const handleCountCardClick = async (date, itemType, itemTypeVerbose) => {
    try {
      // Open dialog immediately with loading state
      setSelectedDateType({
        date: date,
        itemType: itemType,
        itemTypeVerbose: itemTypeVerbose
      });
      setDateTypeTasks([]);
      setDateTypeDialogOpen(true);
      setDialogLoading(true);
      
      // Fetch tasks with current filters
      const filters = buildFilters;
      const tasks = await fetchTasksByDateAndType(date, itemType, filters);
      setDateTypeTasks(tasks);
    } catch (error) {
      console.error('Error fetching tasks by date and type:', error);
      openErrorModal('Error', 'Failed to fetch tasks for this date and type');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleEventDrop = async (arg) => {
    // Prevent drag if task is completed, cancelled, or is a count event
    if (arg.event.extendedProps.isCountEvent || ['completed', 'cancelled'].includes(arg.event.extendedProps.status?.toLowerCase())) {
      arg.revert();
      return;
    }

    try {
      const updatedTask = {
        ...arg.event.extendedProps,
        start_time: arg.event.start,
        end_time: arg.event.end,
        lead: arg.event.extendedProps.lead?.id,
        assigned_to: arg.event.extendedProps.assigned_to?.id,
        asset: arg.event.extendedProps.asset?.id,
      };
      updatedTask.assigned_to = updatedTask.assigned_to.id;
      const response = await updateTask(arg.event.id, updatedTask);
      
      // Refresh data in background (non-blocking)
      const filters = buildFilters;
      // Fetch counts first (faster)
      fetchCalenderTaskCounts(filters).then((counts) => {
        if (counts) {
          setTaskCounts(counts);
        }
      });
      
      // Fetch full tasks (for sidebar)
      fetchCalenderTasks(filters);
    } catch (error) {
      arg.revert();
    }
  };

  const handleCloseDialog = async () => {
    setCreateDialogOpen(false);
    setSelectedDate(null);
    setSelectedEvent(null);
    
    // Refresh data in background (non-blocking)
    const filters = buildFilters;
    // Fetch counts first (faster)
    fetchCalenderTaskCounts(filters).then((counts) => {
      if (counts) {
        setTaskCounts(counts);
      }
    });
    
    // Fetch full tasks (for sidebar)
    fetchCalenderTasks(filters);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    calendarRef.current.getApi().changeView(view);
  };

  const handlePrev = () => {
    const api = calendarRef.current.getApi();
    api.prev();
    setCurrentDate(api.getDate());
  };

  const handleNext = () => {
    const api = calendarRef.current.getApi();
    api.next();
    setCurrentDate(api.getDate());
  };

  const handleToday = () => {
    const api = calendarRef.current.getApi();
    api.today();
    setCurrentDate(api.getDate());
  };

  // Function to create custom popover header
  const createPopoverHeader = (date) => {
    const header = document.createElement('div');
    header.style.cssText = `
      padding: 12px 16px;
      background-color: #F8FAFC;
      border-bottom: 1px solid #E2E8F0;
      margin: -12px -12px 12px -12px;
      border-radius: 12px 12px 0 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;
    header.innerHTML = `
      <span style="font-size: 0.875rem; font-weight: 600; color: #111827;">
        ${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </span>
      <span style="color: #6B7280; cursor: pointer; font-size: 1rem;" onclick="this.closest('.custom-fc-popover').remove()">×</span>
    `;
    return header;
  };

  // Function to create event card element using React component
  const createEventCard = (event) => {
    const eventEl = document.createElement('div');
    eventEl.style.marginBottom = '12px';
    
    const eventData = {
      ...event.extendedProps,
      title: event.title,
      start_time: event.start,
      end_time: event.end,
    };
    
    // Create React root and render TimelineItemCard
    const root = ReactDOM.createRoot(eventEl);
    root.render(
      React.createElement(TimelineItemCard, {
        item: eventData,
        onClick: (item) => {
          handleCardClick(item);
          document.querySelector('.custom-fc-popover')?.remove();
        }
      })
    );
    
    return eventEl;
  };

  // Function to create and show custom popover
  const createCustomPopover = (info) => {    
    // Aggressively prevent default behavior
    try {
      if (info.jsEvent) {
        info.jsEvent.preventDefault();
        info.jsEvent.stopPropagation();
        info.jsEvent.stopImmediatePropagation();
      }
      
      // Immediately remove any existing popovers
      const removePopovers = () => {
        document.querySelectorAll('.fc-popover, .fc-more-popover, .custom-fc-popover').forEach(el => {
          try {
            if (el && el.parentNode) {
              el.parentNode.removeChild(el);
            }
          } catch (e) {
            console.warn('Error removing popover:', e);
          }
        });
      };
      
      removePopovers();
      
      // Use multiple timeouts to prevent default popover creation
      setTimeout(removePopovers, 1);
      setTimeout(removePopovers, 10);
      setTimeout(removePopovers, 50);
      
      // Create custom popover after a delay
      setTimeout(() => {
        try {
          removePopovers(); // Remove any that might have been created
          
          const events = info.allSegs.map(seg => seg.event);          
          // Create custom popover container
          const popoverEl = document.createElement('div');
          popoverEl.className = 'custom-fc-popover';
          popoverEl.style.cssText = `
            position: fixed;
            z-index: 9999;
            background: white;
            border: 1px solid #E2E8F0;
            border-radius: 12px;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
            padding: 12px;
            max-height: 500px;
            overflow-y: auto;
            min-width: 300px;
            max-width: 400px;
          `;
          
          // Add header
          const header = createPopoverHeader(info.date);
          popoverEl.appendChild(header);
          
          // Add events using React components
          events.forEach(event => {
            const eventCard = createEventCard(event);
            popoverEl.appendChild(eventCard);
          });
          
          // Position popover safely
          let left = 0;
          let top = 0;
          
          if (info.jsEvent && info.jsEvent.target) {
            try {
              const rect = info.jsEvent.target.getBoundingClientRect();
              left = rect.left;
              top = rect.bottom + 5;
              
              // Ensure popover stays within viewport
              const viewportWidth = window.innerWidth;
              const viewportHeight = window.innerHeight;
              
              // Adjust horizontal position if needed
              if (left + 300 > viewportWidth) {
                left = viewportWidth - 320;
              }
              if (left < 10) {
                left = 10;
              }
              
              // Adjust vertical position if needed
              if (top + 300 > viewportHeight) {
                top = rect.top - 310; // Show above instead
              }
              if (top < 10) {
                top = 10;
              }
            } catch (e) {
              console.warn('Error calculating popover position:', e);
              // Fallback to center of screen
              left = (window.innerWidth - 300) / 2;
              top = (window.innerHeight - 300) / 2;
            }
          } else {
            // Fallback positioning
            left = (window.innerWidth - 300) / 2;
            top = (window.innerHeight - 300) / 2;
          }
          
          popoverEl.style.left = left + 'px';
          popoverEl.style.top = top + 'px';
          
          document.body.appendChild(popoverEl);
          
          // Continue removing any default popovers that might appear
          const continuousRemoval = setInterval(() => {
            document.querySelectorAll('.fc-popover, .fc-more-popover').forEach(el => {
              try {
                if (el && el.parentNode && !el.classList.contains('custom-fc-popover')) {
                  el.parentNode.removeChild(el);
                }
              } catch (e) {
                // Ignore errors
              }
            });
          }, 100);
          
          // Remove popover when clicking outside
          setTimeout(() => {
            const handleClickOutside = (e) => {
              if (popoverEl && !popoverEl.contains(e.target)) {
                try {
                  clearInterval(continuousRemoval);
                  if (popoverEl.parentNode) {
                    popoverEl.parentNode.removeChild(popoverEl);
                  }
                } catch (err) {
                  console.warn('Error removing popover:', err);
                }
                document.removeEventListener('click', handleClickOutside);
              }
            };
            document.addEventListener('click', handleClickOutside);
          }, 100);
          
        } catch (error) {
          console.error('Error creating custom popover:', error);
        }
      }, 100);
      
    } catch (error) {
      console.error('Error in createCustomPopover:', error);
    }
  };

  // Custom renderer specifically for popover events
  const renderPopoverEventContent = (eventInfo) => {
    const eventData = {
      ...eventInfo.event.extendedProps,
      id: eventInfo.event.id,
      title: eventInfo.event.title,
      start_time: eventInfo.event.start,
      end_time: eventInfo.event.end,
    };
    
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TimelineItemCard 
          item={eventData}
          onClick={handleCardClick}
        />
      </Box>
    );
  };

  const renderEventContent = (eventInfo) => {
    const eventData = {
      ...eventInfo.event.extendedProps,
      id: eventInfo.event.id,
      title: eventInfo.event.title,
      start_time: eventInfo.event.start,
      end_time: eventInfo.event.end,
    };

    // Get current view
    const view = calendarRef.current?.getApi().view.type;
    
    // Check if we're in a popover context - multiple detection methods
    const isInPopover = eventInfo.el && 
                       (eventInfo.el.classList.contains('popover-event') ||
                        eventInfo.el.closest('.fc-popover') || 
                        eventInfo.el.closest('.fc-more-popover') ||
                        eventInfo.el.parentElement?.closest('.fc-popover') ||
                        // Check if the element has been marked as popover
                        eventInfo.el.dataset.popover === 'true');

    // If in popover, use TimelineItemCard with full details
    if (isInPopover) {
      return renderPopoverEventContent(eventInfo);
    }

    // For month view, use MonthViewCard (showing only task type)
    if (view === 'dayGridMonth') {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            minHeight: '24px',
            maxHeight: '28px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <MonthViewCard 
            item={eventData}
            onClick={eventData.isCountEvent 
              ? () => handleCountCardClick(
                  eventInfo.event.startStr,
                  eventData.item_type,
                  eventData.item_type_verbose
                )
              : handleCardClick
            }
          />
        </Box>
      );
    }

    // For week view, use WeekViewCard (showing only task type)
    if (view === 'timeGridWeek') {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            minHeight: '24px',
            maxHeight: '28px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <WeekViewCard 
            item={eventData}
            onClick={eventData.isCountEvent 
              ? () => handleCountCardClick(
                  eventInfo.event.startStr,
                  eventData.item_type,
                  eventData.item_type_verbose
                )
              : handleCardClick
            }
          />
        </Box>
      );
    }

    // For day view, use DayViewCard
    if (view === 'timeGridDay') {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            minHeight: '60px',
            maxHeight: '70px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <DayViewCard 
            item={eventData}
            onClick={handleCardClick}
          />
        </Box>
      );
    }

    // For list view, use TimelineItemCard
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: '50px',
          maxHeight: '60px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TimelineItemCard 
          item={eventData}
          onClick={handleCardClick}
        />
      </Box>
    );
  };


  return (
    <Box sx={{ 
      minHeight: '100vh', 
      p: { xs: 1, md: 2 }, 
      backgroundColor: '#F8FAFC',
      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
      position: 'relative'
    }}>
      <Grid container spacing={1}>
        {/* Sidebar Section */}
        <Grid item xs={12} lg={2.5}>
          <Box sx={{ 
            backgroundColor: '#fff', 
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            height: calendarHeight,
            minHeight: calendarHeight,
            maxHeight: calendarHeight,
            overflow: 'auto',
            position: 'sticky',
            top: 16,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ borderBottom: 1, borderColor: '#E2E8F0' }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    fontSize: '0.8125rem', // Reduced from 0.875rem
                    minHeight: '36px', // Reduced height
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#64748B',
                    '&.Mui-selected': {
                      color: '#1E293B',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#3B82F6',
                    height: '2px',
                  },
                }}
              >
                <Tab label="Today's Tasks" />
                <Tab label="Backlog" />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Box sx={{ borderBottom: 1, borderColor: '#E2E8F0' }}>
                <Tabs 
                  value={activeSubTab} 
                  onChange={(e, newValue) => setActiveSubTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      fontSize: '0.6875rem', // Reduced from 0.75rem
                      minHeight: '28px', // Reduced height
                      textTransform: 'none',
                      fontWeight: 500,
                      minWidth: 'auto',
                      px: 1.5,
                      color: '#64748B',
                      '&.Mui-selected': {
                        color: '#1E293B',
                      },
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#3B82F6',
                      height: '2px',
                    },
                  }}
                >
                  <Tab label="All" />
                  <Tab label="Calls" />
                  <Tab label="Follow-ups" />
                  <Tab label="Site Visits" />
                  <Tab label="General" />
                </Tabs>
              </Box>
            )}
            
            {activeTab === 0 ? (
              <Box sx={{ position: 'relative', flex: 1, overflow: 'auto' }}>
                {(loading || isInitialLoad) && calenderTasks.length === 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 5,
                      backdropFilter: 'blur(2px)',
                    }}
                  >
                    <CircularProgress size={40} sx={{ color: '#3B82F6' }} />
                    <Typography variant="body2" sx={{ mt: 2, color: '#64748B', textAlign: 'center', px: 2 }}>
                      {loadingTimeout ? 'Loading is taking longer than usual...' : 'Loading tasks...'}
                    </Typography>
                    {loadingTimeout && (
                      <Typography variant="caption" sx={{ mt: 1, color: '#94A3B8', textAlign: 'center', px: 2 }}>
                        Check browser console (F12) for errors
                      </Typography>
                    )}
                  </Box>
                )}
                <Timeline 
                  title="Today's Tasks"
                  subtitle={`${todayTasks.length} tasks scheduled for today`}
                  events={todayTasks.map(task => ({
                    ...task,
                    with_person: task.assigned_to?.name || task.owner?.name
                  }))}
                  onEventClick={(event) => {
                    setSelectedEvent(event);
                    setCreateDialogOpen(true);
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ position: 'relative', flex: 1, overflow: 'auto' }}>
                {(loading || isInitialLoad) && calenderTasks.length === 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 5,
                      backdropFilter: 'blur(2px)',
                    }}
                  >
                    <CircularProgress size={40} sx={{ color: '#3B82F6' }} />
                    <Typography variant="body2" sx={{ mt: 2, color: '#64748B', textAlign: 'center', px: 2 }}>
                      {loadingTimeout ? 'Loading is taking longer than usual...' : 'Loading tasks...'}
                    </Typography>
                    {loadingTimeout && (
                      <Typography variant="caption" sx={{ mt: 1, color: '#94A3B8', textAlign: 'center', px: 2 }}>
                        Check browser console (F12) for errors
                      </Typography>
                    )}
                  </Box>
                )}
                <Timeline 
                  title="Backlog Tasks"
                  subtitle={`${backlogTasks.length} overdue tasks`}
                  events={backlogTasks.map(task => ({
                    ...task,
                    with_person: task.assigned_to?.name || task.owner?.name
                  }))}
                  onEventClick={(event) => {
                    setSelectedEvent(event);
                    setCreateDialogOpen(true);
                  }}
                />
              </Box>
            )}
          </Box>
        </Grid>

        {/* Calendar Section */}
        <Grid item xs={12} lg={9.5}>
          <Box 
            data-calendar-container="true"
            sx={{ 
              backgroundColor: '#fff',
              borderRadius: '16px',
              border: '1px solid #E2E8F0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              overflow: 'hidden',
              height: 'auto',
              width: '100%',
              position: 'relative'
            }}
          >
            {/* Loading overlay for calendar section - show on initial load or when filters are applied */}
            {((isInitialLoad && taskCounts.length === 0) || isFilterLoading) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '16px',
                  backdropFilter: 'blur(2px)',
                }}
              >
                <CircularProgress size={50} sx={{ color: '#3B82F6' }} />
                <Typography variant="body2" sx={{ mt: 2, color: '#64748B', fontWeight: 500 }}>
                  {isFilterLoading ? 'Refreshing calendar data...' : 'Loading calendar data...'}
                </Typography>
                {isFilterLoading && (selectedAgent || selectedTeam) && (
                  <Typography variant="caption" sx={{ mt: 1, color: '#94A3B8', textAlign: 'center', px: 2 }}>
                    {selectedAgent ? 'Filtering by agent...' : selectedTeam ? 'Filtering by team...' : ''}
                  </Typography>
                )}
              </Box>
            )}
            <CalendarWrapper currentView={currentView}>
              <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 0,
                p: { xs: 1.5, md: 3 }, // Reduced padding
                borderBottom: '1px solid #E2E8F0',
                backgroundColor: '#FAFBFC'
              }}
            >
              {/* Left: Navigation */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ButtonGroup variant="outlined" size="small">
                  <Button 
                    onClick={handlePrev}
                    sx={{ 
                      borderColor: '#CBD5E1',
                      color: '#475569',
                      borderRadius: '8px 0 0 8px',
                      '&:hover': { 
                        borderColor: '#94A3B8',
                        backgroundColor: '#F8FAFC'
                      }
                    }}
                  >
                    <ChevronLeftIcon />
                  </Button>
                  <Button 
                    onClick={handleNext}
                    sx={{ 
                      borderColor: '#CBD5E1',
                      color: '#475569',
                      borderRadius: '0 8px 8px 0',
                      '&:hover': { 
                        borderColor: '#94A3B8',
                        backgroundColor: '#F8FAFC'
                      }
                    }}
                  >
                    <ChevronRightIcon />
                  </Button>
                </ButtonGroup>
                <Button 
                  onClick={handleToday}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    borderColor: '#CBD5E1',
                    color: '#475569',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    ml: 1,
                    px: 3,
                    '&:hover': { 
                      borderColor: '#94A3B8',
                      backgroundColor: '#F8FAFC'
                    }
                  }}
                >
                  Today
                </Button>
              </Box>

              {/* Center: Month/Year */}
              <Typography variant="h4" sx={{ 
                fontWeight: 800,
                color: '#1E293B',
                fontSize: { xs: '1.25rem', md: '1.75rem' },
                background: 'linear-gradient(135deg, #1E293B 0%, #475569 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {format(currentDate, 'MMMM yyyy')}
              </Typography>

              {/* Right: Filters */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isFilterLoading && (
                  <CircularProgress size={20} sx={{ color: '#3B82F6', mr: -1 }} />
                )}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="agent-filter-label">Agent</InputLabel>
                  <Select
                    labelId="agent-filter-label"
                    id="agent-filter"
                    value={selectedAgent}
                    label="Agent"
                    disabled={!!selectedTeam || loadingAgents}
                    onChange={(e) => {
                      setSelectedAgent(e.target.value);
                      setSelectedTeam(''); // Clear team when agent is selected
                    }}
                    sx={{
                      backgroundColor: '#FFFFFF',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#CBD5E1',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#94A3B8',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3B82F6',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#F9FAFB',
                        color: '#9CA3AF',
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>All Agents</em>
                    </MenuItem>
                    {agents.map((agent) => {
                      const agentId = agent.agent_id || agent.id;
                      const agentName = agent.agent_name || agent.name || 'Unknown';
                      const teamName = agent.team_name || '';
                      return (
                        <MenuItem key={agentId} value={agentId}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography component="span" sx={{ fontWeight: 500 }}>
                              {agentName}
                            </Typography>
                            {teamName && (
                              <Typography 
                                component="span" 
                                sx={{ 
                                  ml: 1, 
                                  fontSize: '0.75rem', 
                                  color: '#94A3B8',
                                  fontWeight: 400,
                                  opacity: 0.7
                                }}
                              >
                                {teamName}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="team-filter-label">Team</InputLabel>
                  <Select
                    labelId="team-filter-label"
                    id="team-filter"
                    value={selectedTeam}
                    label="Team"
                    disabled={!!selectedAgent || loadingTeams}
                    onChange={(e) => {
                      setSelectedTeam(e.target.value);
                      setSelectedAgent(''); // Clear agent when team is selected
                    }}
                    sx={{
                      backgroundColor: '#FFFFFF',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#CBD5E1',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#94A3B8',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3B82F6',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#F9FAFB',
                        color: '#9CA3AF',
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>All Teams</em>
                    </MenuItem>
                    {teams.length === 0 && !loadingTeams && (
                      <MenuItem disabled>
                        No teams available
                      </MenuItem>
                    )}
                    {teams.map((team) => {
                      const teamId = team.id || team.team_id;
                      const teamName = team.name || 'Unnamed Team';
                      return (
                        <MenuItem key={teamId} value={teamId}>
                          {teamName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <React.Suspense fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
              </Box>
            }>
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                dayMaxEvents={8}
                slotEventOverlap={true}
                headerToolbar={false}
                initialView="dayGridMonth"
                editable={true}
                selectable={true}
                selectMirror={true}
                weekends={true}
                events={events}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                eventDrop={handleEventDrop}
                eventContent={renderEventContent}
                eventDidMount={(eventInfo) => {
                  const checkPopover = (attempts = 0) => {
                    const isInPopover = eventInfo.el.closest('.fc-popover');
                    if (isInPopover) {
                      eventInfo.el.classList.add('popover-event');
                      eventInfo.el.dispatchEvent(new CustomEvent('popover-detected'));
                    } else if (attempts < 5) {
                      setTimeout(() => checkPopover(attempts + 1), 50 * (attempts + 1));
                    }
                  };
                  checkPopover();
                }}
                height="auto"
                viewDidMount={(view) => setCurrentView(view.view.type)}
                views={{
                  dayGridMonth: {
                    dayMaxEventRows: 8
                  },
                  timeGridWeek: {
                    // slotMinTime: "07:00:00",
                    // slotMaxTime: "24:00:00",
                    slotDuration: "01:00:00",
                    dayMaxEvents: 8, // show 8 events before "+ more"
                    dayHeaderFormat: {
                      weekday: 'short'
                    },
                    dayHeaderContent: (arg) => {
                      const date = new Date(arg.date);
                      const day = date.getDate().toString().padStart(2, '0');
                      const month = (date.getMonth() + 1).toString().padStart(2, '0');
                      const year = date.getFullYear();
                      return {
                        html: `
                          <div style="text-align: center;">
                            <div style="font-weight: 700; font-size: 0.6875rem; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.5px; color: #475569;">${arg.date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                            <div style="font-size: 0.6875rem; color: #475569; font-weight: 600;">${day}-${month}-${year}</div>
                          </div>
                        `
                      };
                    }
                  },
                  timeGridDay: {
                    // slotMinTime: "07:00:00",
                    // slotMaxTime: "24:00:00",
                    slotDuration: "01:00:00",
                    dayMaxEvents: 8
                  }
                }}
                allDaySlot={false}
                stickyHeaderDates={true}
                eventMinHeight={50}
                contentHeight="auto"
                aspectRatio={null}
                expandRows={true}
                dayHeaderFormat={{
                  weekday: 'short'
                }}
                slotLabelFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  omitZeroMinute: true,
                  hour12: true
                }}
                nowIndicator={true}
                eventDisplay="block"
                eventOverlap={true}
                titleFormat={{
                  month: 'long',
                  year: 'numeric'
                }}
                moreLinkClick={(info) => {
                  createCustomPopover(info);
                  return 'none';
                }}
                moreLinkClassNames="custom-more-link"
                eventTimeFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                }}
              moreLinkContent={(arg) => `+${arg.num} more`}
              dayPopoverFormat={false}
              />
            </React.Suspense>
            </CalendarWrapper>
          </Box>
        </Grid>
      </Grid>
      <CreateNewTask
        open={createDialogOpen}
        onClose={handleCloseDialog}
        initialData={selectedEvent}
        defaultDate={selectedDate}
        source="task_calendar"
      />
      
      {/* Dialog to display tasks for specific date and type */}
      <Dialog
        open={dateTypeDialogOpen}
        onClose={() => setDateTypeDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #E2E8F0',
          pb: 2
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
              {selectedDateType?.itemTypeVerbose || 'Tasks'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
              {selectedDateType?.date && format(new Date(selectedDateType.date), 'MMMM dd, yyyy')} • {dateTypeTasks.length} task{dateTypeTasks.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setDateTypeDialogOpen(false)}
            sx={{ color: '#64748B' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : dateTypeTasks.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" sx={{ color: '#64748B' }}>
                No tasks found for this date and type
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dateTypeTasks.map((task) => (
                <TimelineItemCard
                  key={task.id}
                  item={{
                    ...task,
                    with_person: task.assigned_to?.name || task.owner?.name
                  }}
                  onClick={(item) => {
                    setSelectedEvent(item);
                    setDateTypeDialogOpen(false);
                    setCreateDialogOpen(true);
                  }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, borderTop: '1px solid #E2E8F0' }}>
          <Button 
            onClick={() => setDateTypeDialogOpen(false)}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              color: '#64748B'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


export default TaskCalendar; 